<template>
  <AttrSelect
    label="指针光源"
    :value="shape.attrs.pointerRegionNum ?? 2"
    :options="pointerRegionOptions"
    :disabled="!!attrDisabled"
    @change="(val:any)=>setPointerType(val)"
  />

  <div class="flex justify-center">
    <div
      class="w-[10.625rem] h-[10.625rem] border border-[#4A4A4A] rounded-full grid overflow-hidden"
      :class="{
        'grid-cols-2 grid-rows-1': shape.attrs.pointerRegionNum == 2,
        'grid-cols-2 grid-rows-2': shape.attrs.pointerRegionNum == 4,
        'grid-cols-1 grid-rows-1': shape.attrs.pointerRegionNum == 1,
        _num2: shape.attrs.pointerRegionNum == 2,
        _num4: shape.attrs.pointerRegionNum == 4
      }"
    >
      <div
        v-for="index in _pointerRegionNums"
        :key="index"
        class="flex items-center justify-center h-full"
      >
        <ImagePicker
          class="!h-[75%]"
          :img="pointers[index - 1]"
          :border="false"
          :deletable="index != 1"
          :size="42"
          :icon-size="24"
          :drag-data="{
            pointerRegionNum: shape.attrs.pointerRegionNum,
            pointerIdx: index
          }"
          @change="(image: string) => onImageUpdate(image, `pointer${index-1}` as 'pointer0')"
          @delete="() => emits('delete', `pointer${index-1}` as 'pointer0')"
          @drop="(e: DragEvent) => handleDrop(e, index)"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { withTempFile } from '@/hooks/use-temp-file';
import { Shape } from '@/services/shape';
import { ResourceKey } from '@/services/shape/attributes';
import { useEditorStore } from '@/store/use-editor-store';
import { computed, PropType } from 'vue';

const props = defineProps({
  shape: {
    type: Object as PropType<Shape>,
    required: true
  }
});

const editorStore = useEditorStore();

const attrDisabled = computed(() => editorStore.attrDisabled);

const pointerRegionOptions = [
  { label: '无', value: 1 },
  { label: '两象限', value: 2 },
  { label: '四象限', value: 4 }
];

const setPointerType = (val: number) => {
  props.shape.setAttrs({ pointerRegionNum: val });
  if (val < 2) {
    props.shape.setAttrs({ renderRule: 'alwaysShow' });
  } else {
    props.shape.setAttrs({ renderRule: 'hideWhenOutRange' });
  }
};

const _pointerRegionNums = computed(() => {
  const num = props.shape.attrs.pointerRegionNum ?? 1;
  if (num == 2) {
    return [2, 1];
  } else if (num == 4) {
    return [4, 1, 3, 2];
  } else {
    return [1];
  }
});

const pointers = computed(() => {
  const _pointers: string[] = [];
  const shape = props.shape;
  (['pointer0', 'pointer1', 'pointer2', 'pointer3'] as ResourceKey[]).forEach(
    (resourceName) => {
      const img = shape.getResources({
        resourceName,
        colorGroup: editorStore.themeColor?.id ?? '',
        base64: true
      })?.[0];
      _pointers.push(img);
    }
  );

  return _pointers;
});

const onImageUpdate = async (image: string, attrName: ResourceKey) => {
  emits('onImageUpdate', image, attrName);
};

const emits = defineEmits<{
  (event: 'onImageUpdate', image: string, resourceName: ResourceKey): void;
  (event: 'delete', resourceName: ResourceKey): void;
}>();

const handleDrop = async (e: DragEvent, _currentIdx: number) => {
  if (!e.dataTransfer) return;
  const _image = e.dataTransfer.getData('image');
  const _pointerRegionNum = parseInt(
    e.dataTransfer.getData('pointerRegionNum')
  );
  const _pointedIdx = parseInt(e.dataTransfer.getData('pointerIdx'));
  console.log(_pointedIdx, _currentIdx);
  // 数据异常 不处理
  if (!_image || isNaN(_pointerRegionNum) || isNaN(_pointedIdx) || !_currentIdx)
    return;
  if (_pointedIdx === _currentIdx) return;

  if (pointers.value[_currentIdx - 1] && pointers.value[_pointedIdx - 1]) {
    const imgCur = pointers.value[_currentIdx - 1];
    const imgPtr = pointers.value[_pointedIdx - 1];
    withTempFile(
      imgPtr,
      (filename) =>
        onImageUpdate(filename, `pointer${_currentIdx - 1}` as 'pointer0'),
      false
    );
    withTempFile(
      imgCur,
      (filename) =>
        onImageUpdate(filename, `pointer${_pointedIdx - 1}` as 'pointer0'),
      false
    );
  }
};
</script>

<style lang="less" scoped>
._num2 {
  background: center no-repeat url(@/assets/light-region-2.webp);
  & > div:nth-child(1) {
    justify-content: flex-end;
    padding-right: 12px;
  }
  & > div:nth-child(2) {
    justify-content: flex-start;
    padding-left: 12px;
  }
}
._num4 {
  background: center no-repeat url(@/assets/light-region-4.webp);
  & > div:nth-child(2n-1) {
    justify-content: flex-end;
    padding-right: 8px;
  }
  & > div:nth-child(2n) {
    justify-content: flex-start;
    padding-left: 8px;
  }
  & > div:nth-child(-n + 2) {
    align-items: flex-end;
    padding-bottom: 1px;
  }
  & > div:nth-child(n + 3) {
    align-items: flex-start;
    padding-top: 4px;
  }
}
</style>
